import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface DailyVotes {
  date: string;
  total: number;
  authenticated: number;
  anonymous: number;
}

interface Analytics {
  total_votes: number;
  total_users: number;
  votes_today: number;
  votes_this_week: number;
  daily_votes: DailyVotes[];
}

export default function AnalyticsOverview() {
  const { authToken } = useAuth();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/analytics`, { headers });
      if (!res.ok) throw new Error(`Failed to fetch analytics: ${res.status}`);
      const data = await res.json();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-arena-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-arena-muted">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchAnalytics} className="ml-3 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) return null;

  const dailyVotes = analytics.daily_votes || [];
  const maxDaily = Math.max(...dailyVotes.map((d) => d.total), 1);

  const formatDay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const statCards = [
    { label: 'Total Votes', value: analytics.total_votes },
    { label: 'Total Users', value: analytics.total_users },
    { label: 'Votes Today', value: analytics.votes_today },
    { label: 'Votes This Week', value: analytics.votes_this_week },
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="card p-5 text-center">
            <div className="text-3xl font-bold font-mono text-arena-text">
              {stat.value.toLocaleString()}
            </div>
            <div className="text-sm text-arena-muted mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Daily Votes Chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-arena-text">Votes - Last 7 Days</h3>
          <button onClick={fetchAnalytics} className="btn btn-secondary text-sm">
            Refresh
          </button>
        </div>

        {dailyVotes.length === 0 ? (
          <p className="text-arena-muted text-center py-8">No vote data available yet.</p>
        ) : (
          <div className="space-y-3">
            {dailyVotes.map((day) => {
              const authPercent = day.total > 0 ? (day.authenticated / day.total) * 100 : 0;
              const anonPercent = day.total > 0 ? (day.anonymous / day.total) * 100 : 0;
              const barWidth = (day.total / maxDaily) * 100;

              return (
                <div key={day.date} className="flex items-center gap-3">
                  {/* Day label */}
                  <div className="w-32 text-sm text-arena-muted text-right shrink-0">
                    {formatDay(day.date)}
                  </div>

                  {/* Bar */}
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 h-7 bg-arena-surface rounded overflow-hidden relative">
                      {day.total > 0 && (
                        <div
                          className="h-full flex rounded overflow-hidden transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        >
                          {/* Authenticated portion */}
                          <div
                            className="h-full bg-arena-accent"
                            style={{ width: `${authPercent}%` }}
                            title={`Authenticated: ${day.authenticated}`}
                          />
                          {/* Anonymous portion */}
                          <div
                            className="h-full bg-arena-accent/40"
                            style={{ width: `${anonPercent}%` }}
                            title={`Anonymous: ${day.anonymous}`}
                          />
                        </div>
                      )}
                    </div>

                    {/* Count */}
                    <div className="w-12 text-sm font-mono text-arena-text text-right shrink-0">
                      {day.total}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        {dailyVotes.length > 0 && (
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-arena-border">
            <div className="flex items-center gap-2 text-sm text-arena-muted">
              <div className="w-3 h-3 rounded-sm bg-arena-accent" />
              Authenticated
            </div>
            <div className="flex items-center gap-2 text-sm text-arena-muted">
              <div className="w-3 h-3 rounded-sm bg-arena-accent/40" />
              Anonymous
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
